import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  AuthContextType, 
  AuthState, 
  AuthResponse,
  LoginRequest, 
  User,
  TOKEN_KEYS 
} from '@/types/auth';
import { 
  tokenStorage, 
  getMenuAccess, 
  canLogin, 
  formatAuthError 
} from '@/utils/auth';
import { API_BASE_URL } from '@/lib/api';

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeUser = (user: User): User => {
  const isAdminRole = user.designation === 'SUPER_ADMIN' || user.designation === 'ADMINISTRATOR';

  return {
    ...user,
    assignedBranchIds: user.assignedBranchIds?.length ? user.assignedBranchIds : (user.branchId ? [user.branchId] : []),
    isAdmin: user.isAdmin ?? isAdminRole,
    isSuperAdmin: user.isSuperAdmin ?? user.designation === 'SUPER_ADMIN'
  };
};

// Auth provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  
  // Initial state
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Check if user is authenticated
  const checkAuth = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const { accessToken, refreshToken: storedRefreshToken, rememberMe } = tokenStorage.getTokens();
      const storedUser = tokenStorage.getUser();
      const user = storedUser ? normalizeUser(storedUser) : null;

      // If no tokens or user, clear state
      if (!accessToken || !storedRefreshToken || !user) {
        setState({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
        return;
      }

      // Check if token is expired
      if (tokenStorage.isTokenExpired(accessToken)) {
        // Try to refresh token
        if (storedRefreshToken) {
          await refreshAuthToken();
          return;
        } else {
          // No refresh token, logout
          logout();
          return;
        }
      }

      // Check remember me validity
      if (rememberMe && !tokenStorage.isRememberMeValid()) {
        logout();
        return;
      }

      // Set authenticated state
      setState({
        user,
        accessToken,
        refreshToken: storedRefreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    }
  };

  // Login function
  const login = async (credentials: LoginRequest) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const response = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/login`, {
        username: credentials.username,
        password: credentials.password
      }, {
        timeout: 15000
      });

      const { user: rawUser, accessToken, refreshToken } = response.data.data;
      const user = normalizeUser(rawUser);

      // Check if user can login
      if (!canLogin(user.designation)) {
        throw new Error('Your role does not have login access.');
      }

      // Check if user is active
      if (!user.isActive) {
        throw new Error('Your account is deactivated. Please contact administrator.');
      }

      // Save tokens and user data
      const rememberMe = credentials.rememberMe ?? true; // Default to true
      tokenStorage.saveTokens(accessToken, refreshToken, rememberMe);
      tokenStorage.saveUser(user, rememberMe);

      if (rememberMe) {
        tokenStorage.setRememberMeTimestamp();
      }

      // Update state
      setState({
        user,
        accessToken,
        refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      // Redirect to dashboard
      navigate('/dashboard');

    } catch (error) {
      const errorMessage = formatAuthError(error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      throw new Error(errorMessage);
    }
  };

  // Refresh token function
  const refreshAuthToken = async () => {
    try {
      const { refreshToken: currentRefreshToken } = tokenStorage.getTokens();
      
      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/refresh`, {
        refreshToken: currentRefreshToken
      }, {
        timeout: 15000
      });

      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      const storedUser = tokenStorage.getUser();
      const user = storedUser ? normalizeUser(storedUser) : null;

      if (!user) {
        throw new Error('User data not found');
      }

      // Save new tokens
      const rememberMe = localStorage.getItem(TOKEN_KEYS.REMEMBER_ME) === 'true';
      tokenStorage.saveTokens(accessToken, newRefreshToken, rememberMe);

      // Update state
      setState(prev => ({
        ...prev,
        accessToken,
        refreshToken: newRefreshToken,
        isLoading: false,
        error: null
      }));

    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const { accessToken } = tokenStorage.getTokens();
      
      // Call logout API if token exists
      if (accessToken) {
        await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
          timeout: 15000,
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear tokens and state
      tokenStorage.clearTokens();
      
      setState({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });

      // Force page reload to clear all component state
      window.location.href = '/';
    }
  };

  // Clear error
  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  // Get menu access for current user
  const getCurrentUserMenuAccess = () => {
    if (!state.user) return null;
    return getMenuAccess(state.user.designation);
  };

  // Context value
  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    refreshAuthToken,
    clearError,
    checkAuth,
    getMenuAccess: getCurrentUserMenuAccess
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
